import { Box, InputAdornment, Stack, Switch } from '@mui/material';
import { upperFirst } from 'lodash';
import { type ReactNode, useId, useState } from 'react';

import { FormField, Text, api, colors, isDefined } from 'common';

import { BlockValueTemplateInnerField } from './CodeMirror/FlowTemplate';
import { BlockInnerSelectField } from './SelectField';
import { BlockInnerTextField } from './TextField';
import { type ConfigFieldProps } from './types';
import { useSelectedNodeField } from './useSelectedNodeField';
import { useReactFlowViewContext } from '../../context/context';

type UnitType = api.FlowDefinition.LookbackDurationUnit | api.FlowDefinition.DurationUnits;
interface FieldProps<T extends UnitType> extends ConfigFieldProps {
  required?: boolean;
  toggleLabel?: string;
  valueLabel?: string;
  valueFieldNameSuffix?: string;
  unitFieldNameSuffix: string;
  allowedUnits: T[];
  defaultUnit?: T;
  valueHelperText?: ReactNode;
  variant?: 'Numeric' | 'BlockValueTemplate';
}

export function BlockDurationField<T extends UnitType>(props: FieldProps<T>) {
  const {
    fieldName,
    valueFieldNameSuffix = 'value',
    unitFieldNameSuffix,
    toggleLabel,
    valueLabel,
    allowedUnits,
    required = true,
    defaultUnit = api.FlowDefinition.DurationUnits.Days,
    valueHelperText,
    variant = 'Numeric',
  } = props;
  const { isEditable } = useReactFlowViewContext();
  const { value, visibleError, setValue } = useSelectedNodeField<
    Record<string, T | number | string> | undefined
  >(fieldName);
  /**
   * IMPORTANT: visibleError is a string or object or undefined. The validation type comes in as a string, which
   * is not true if the schema validation returns an object. In this case, we can get errors like:
   * { value: 'required', units: 'required' }
   * { unit: 'required' }
   * 'Must be less than 3600 seconds'
   * We typecast here to protect from hidden errors in the `FormHelperText` component.
   */
  const error: string | object | undefined = visibleError;
  const { visibleError: visibleValueError } = useSelectedNodeField<
    Record<string, T | number | string> | undefined
  >(`${fieldName}.${valueFieldNameSuffix}`);
  const { visibleError: visibleUnitError } = useSelectedNodeField<T>(
    `${fieldName}.${unitFieldNameSuffix}`
  );
  const [durationIsVisible, setDurationIsVisible] = useState(required || isDefined(value));
  const unitOptions = allowedUnits.map((unit) => ({
    label: upperFirst(unit.toLowerCase()),
    value: unit,
  }));
  const textFieldId = useId();

  return (
    <Stack gap={1}>
      {!required && (
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Text bold>{toggleLabel || 'Enable Lookback duration'}</Text>
          <Switch
            checked={durationIsVisible}
            onChange={async () => {
              const shouldUseDuration = !durationIsVisible;
              if (shouldUseDuration) {
                setValue({
                  [valueFieldNameSuffix]: 10,
                  [unitFieldNameSuffix]: defaultUnit,
                });
                setDurationIsVisible(shouldUseDuration);
              } else {
                setDurationIsVisible(shouldUseDuration);
                setValue(undefined);
              }
            }}
            disabled={!isEditable}
            aria-label={toggleLabel || 'Enable Lookback duration'}
          />
        </Stack>
      )}
      {durationIsVisible && (
        <Box>
          <FormField
            id={textFieldId}
            label={valueLabel || 'Lookback Duration'}
            helperText={valueHelperText}
            error={
              visibleValueError ??
              visibleUnitError ??
              (typeof error === 'string' ? error : undefined)
            }
          >
            {variant === 'BlockValueTemplate' && (
              <Stack
                direction="row"
                width="100%"
                sx={{
                  border: '1px solid',
                  borderColor: visibleValueError ? colors.error.default : colors.border.default,
                  borderRadius: 1,
                  paddingRight: 1,
                  justifyContent: 'space-between',
                }}
              >
                <Box flex={1} maxWidth="75%">
                  <BlockValueTemplateInnerField
                    fieldId={textFieldId}
                    fieldName={`${fieldName}.${valueFieldNameSuffix}`}
                    aria-label={valueLabel || 'Lookback Duration'}
                    required={required}
                    variant="borderless"
                  />
                </Box>
                <Box>
                  <BlockInnerSelectField
                    fieldName={`${fieldName}.${unitFieldNameSuffix}`}
                    required={required}
                    options={unitOptions}
                    aria-label="Units"
                    clearable={false}
                    variant="borderless"
                  />
                </Box>
              </Stack>
            )}
            {variant === 'Numeric' && (
              <BlockInnerTextField
                fieldId={textFieldId}
                fieldName={`${fieldName}.${valueFieldNameSuffix}`}
                aria-label={valueLabel || 'Lookback Duration'}
                required={required}
                type="number"
                slotProps={{
                  input: {
                    sx: {
                      paddingRight: 1,
                    },
                    endAdornment: (
                      <InputAdornment position="end">
                        <BlockInnerSelectField
                          fieldName={`${fieldName}.${unitFieldNameSuffix}`}
                          required={required}
                          options={unitOptions}
                          aria-label="Units"
                          clearable={false}
                          variant="borderless"
                        />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            )}
          </FormField>
        </Box>
      )}
    </Stack>
  );
}
